import React from 'react';

import classNames from 'classnames';
import { findKey } from 'lodash';
import { DateTime } from 'luxon';

import { CALL_RESCUE_TEST_STATUS } from '../constants';
import { useCallRescueMonitorContext } from '../context';

const List: React.FC = () => {
  const { state } = useCallRescueMonitorContext();
  const { callRescueTests } = state;

  const RenderStandard: React.FC = () => {
    return (
      <div className="sm:flex flex-grow hidden mt-6 overflow-auto">
        <table className="min-w-full divide-y divide-white-2.0 flex-grow rounded">
          <thead>
            <tr>
              <th className="data-cell-header rounded-tl">Account name</th>
              <th className="data-cell-header">Phone called</th>
              <th className="data-cell-header">Phone calling</th>
              <th className="data-cell-header">Call status</th>
              <th className="data-cell-header">Last update</th>
              <th className="data-cell-header rounded-tr">Retry count</th>
            </tr>
          </thead>
          <tbody
            className="bg-white.0.0 divide-y divide-white-2.0"
            data-testid="call-rescue-test-list"
          >
            {callRescueTests.map((callRescueTest, index) => (
              <tr
                key={callRescueTest.id}
                className={classNames(index % 2 === 0 ? 'bg-white-0.0' : 'bg-white-1.0')}
                data-testid={`call-rescue-test-${callRescueTest.id}`}
              >
                <td className="data-cell">{callRescueTest.account.name}</td>
                <td className="data-cell">{callRescueTest.phone_called.number}</td>
                <td className="data-cell">{callRescueTest.phone_calling.number}</td>
                <td className="data-cell">
                  <span
                    className={classNames(
                      'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs leading-4 text-white-0.0 font-semibold',
                      callRescueTest.call_status === CALL_RESCUE_TEST_STATUS.SUCCESS &&
                        'bg-secondary-1.0',
                      callRescueTest.call_status === CALL_RESCUE_TEST_STATUS.FAILURE &&
                        'bg-red-1.0',
                      callRescueTest.call_status === CALL_RESCUE_TEST_STATUS.CALLING &&
                        'bg-yellow-1.0',
                      callRescueTest.call_status === CALL_RESCUE_TEST_STATUS.RETRYING &&
                        'bg-orange-1.0',
                      callRescueTest.call_status === CALL_RESCUE_TEST_STATUS.SCHEDULED &&
                        'bg-purple-1.0'
                    )}
                    data-testid={`status-${callRescueTest.call_status}`}
                  >
                    {findKey(
                      CALL_RESCUE_TEST_STATUS,
                      (status) => status === callRescueTest.call_status
                    )}
                  </span>
                </td>
                <td className="data-cell">
                  {callRescueTest.updated_at
                    ? DateTime.fromISO(callRescueTest.updated_at).toFormat('ff')
                    : 'N/A'}
                </td>
                <td className="data-cell">{callRescueTest.retry_count || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const RenderMovil: React.FC = () => {
    return (
      <div className="sm:hidden px-2 mt-6 overflow-auto">
        <ul className="mt-4" data-testid="call-rescue-test-list">
          {callRescueTests.map((callRescueTest) => (
            <li
              key={callRescueTest.id}
              className="bg-white-0.0 flex flex-col justify-between rounded border border-white-2.0 mb-2 px-2 py-4"
              data-testid={`callRescueTest-${callRescueTest.id}`}
            >
              <div className="ml-4">
                <div role="presentation" className="flex flex-col truncate">
                  <p className="oversize my-auto font-bold text-primary-2.0">
                    {callRescueTest.account.name}
                  </p>
                </div>
                <div className="flex flex-grow mt-4">
                  <p className="text-sm font-medium">Phone called: </p>
                  <p className="ml-2 text-sm font-medium truncate">
                    {callRescueTest.phone_called.number}
                  </p>
                </div>
                <div className="flex flex-grow mt-2">
                  <p className="text-sm font-medium">Phone calling: </p>
                  <p className="ml-2 text-sm font-medium truncate">
                    {callRescueTest.phone_calling.number}
                  </p>
                </div>
                <div className="flex flex-grow mt-2">
                  <p className="text-sm font-medium">Status: </p>
                  <span
                    className={classNames(
                      'ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs leading-4 text-white-0.0 font-semibold',
                      callRescueTest.call_status === CALL_RESCUE_TEST_STATUS.SUCCESS &&
                        'bg-secondary-1.0',
                      callRescueTest.call_status === CALL_RESCUE_TEST_STATUS.FAILURE &&
                        'bg-red-1.0',
                      callRescueTest.call_status === CALL_RESCUE_TEST_STATUS.CALLING &&
                        'bg-yellow-1.0',
                      callRescueTest.call_status === CALL_RESCUE_TEST_STATUS.RETRYING &&
                        'bg-orange-1.0',
                      callRescueTest.call_status === CALL_RESCUE_TEST_STATUS.SCHEDULED &&
                        'bg-purple-1.0'
                    )}
                    data-testid={`status-${callRescueTest.call_status}`}
                  >
                    {findKey(
                      CALL_RESCUE_TEST_STATUS,
                      (status) => status === callRescueTest.call_status
                    )}
                  </span>
                </div>
                <div className="flex flex-grow mt-2">
                  <p className="text-sm font-medium">Last update: </p>
                  <p className="ml-2 text-sm font-medium truncate">
                    {callRescueTest.updated_at
                      ? DateTime.fromISO(callRescueTest.updated_at).toFormat('ff')
                      : 'N/A'}
                  </p>
                </div>
                <div className="flex flex-grow mt-2">
                  <p className="text-sm font-medium">Retry count: </p>
                  <p className="ml-2 text-sm font-medium truncate">
                    {callRescueTest.retry_count || 0}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <>
      <RenderStandard />
      <RenderMovil />
    </>
  );
};

export default List;
